/**
 * Make saved phishing markup inert before placing it in an administrator preview.
 *
 * The iframe sandbox remains the primary browser boundary. Removing executable and
 * embedding elements as well prevents stored scripts and nested frames from generating
 * errors or making network requests while an administrator inspects a template. The
 * original HTML is never changed and remains the value saved by the editor.
 */
/**
 * Decode base64 markup (single or double encoded) to HTML string before rendering.
 */
export function ensureDecodedHtml(value) {
  if (!value) return ''
  const text = String(value).trim()
  if (!text) return ''

  // If it already looks like HTML, don't decode
  if (/^\s*<(!doctype|html|head|body|div|table|p|span|style|section|main|header|form|a|img)/i.test(text)) {
    return text
  }

  // Attempt decoding up to 2 levels of base64
  let current = text
  for (let i = 0; i < 2; i++) {
    const clean = current.replace(/\s+/g, '')
    if (clean.length < 4 || clean.length % 4 !== 0 || !/^[A-Za-z0-9+/=]+$/.test(clean)) {
      break
    }
    try {
      const binary = atob(clean)
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
      const decoded = new TextDecoder().decode(bytes).trim()
      if (!decoded || decoded === current) break
      current = decoded
      if (/^\s*<(!doctype|html|head|body|div|table|p|span|style|section|main|header|form|a|img)/i.test(current)) {
        return current
      }
    } catch {
      break
    }
  }

  if (/<[a-z][\s\S]*>/i.test(current)) {
    return current
  }

  return text
}

export function safePreviewHtml(value) {
  const html = ensureDecodedHtml(value)
  if (!html || typeof DOMParser === 'undefined') return html

  const document = new DOMParser().parseFromString(html, 'text/html')
  document
    .querySelectorAll('script, iframe, frame, object, embed, base, meta[http-equiv]')
    .forEach((node) => node.remove())

  for (const element of document.querySelectorAll('*')) {
    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase()
      const compactValue = attribute.value
        .trim()
        .replace(/\s+/g, '')
        .toLowerCase()

      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name)
        continue
      }

      if (
        ['href', 'src', 'action', 'formaction', 'xlink:href'].includes(name) &&
        (compactValue.startsWith('javascript:') ||
          compactValue.startsWith('vbscript:') ||
          compactValue.startsWith('data:text/html'))
      ) {
        element.removeAttribute(attribute.name)
      }
    }
  }

  return `<!doctype html>${document.documentElement.outerHTML}`
}
